import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, Shield, Home } from 'lucide-react';
import { NavigationHeader } from '../../components/ui/NavigationHeader';

const AccountSettingsPasswordManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Wachtwoord moet minimaal 8 tekens bevatten');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Wachtwoord moet minimaal één hoofdletter bevatten');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Wachtwoord moet minimaal één kleine letter bevatten');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Wachtwoord moet minimaal één cijfer bevatten');
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setValidationErrors([]);

    // Validate passwords
    const errors = validatePassword(newPassword);
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setError(updateError.message);
        setLoading(false);
        return;
      }

      setSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
      setLoading(false);

      // Hide success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err: any) {
      setError(err.message || 'Er is een onverwachte fout opgetreden');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-600 mb-6">
          <button 
            onClick={() => navigate('/')}
            className="hover:text-[#7CC363] flex items-center"
          >
            <Home className="h-4 w-4 mr-1" />
            Dashboard
          </button>
          <span className="mx-2">/</span>
          <span className="text-gray-900 font-medium">Wachtwoord Beheer</span>
        </div>

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center mb-2">
            <Shield className="h-8 w-8 text-[#7CC363] mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Wachtwoord Beheer</h1>
          </div>
          <p className="text-gray-600">Wijzig uw wachtwoord om uw account veilig te houden</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Password Change Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Wijzig Wachtwoord</h2>
              
              {/* Success Message */}
              {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Wachtwoord succesvol gewijzigd</p>
                      <p className="text-sm text-green-700 mt-1">Uw wachtwoord is bijgewerkt. U blijft ingelogd.</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                {/* Validation Errors */}
                {validationErrors.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium mb-1">Wachtwoord voldoet niet aan de eisen:</p>
                        <ul className="list-disc list-inside space-y-1">
                          {validationErrors.map((err, index) => (
                            <li key={index}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* New Password Input */}
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Nieuw Wachtwoord
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="pl-10 pr-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7CC363] focus:border-transparent transition"
                      placeholder="••••••••"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      disabled={loading}
                    >
                      {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password Input */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Bevestig Nieuw Wachtwoord
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="pl-10 pr-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7CC363] focus:border-transparent transition"
                      placeholder="••••••••"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      disabled={loading}
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex space-x-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#7CC363] hover:bg-[#6AB352] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7CC363] disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Wachtwoord wijzigen...
                      </>
                    ) : (
                      'Wijzig wachtwoord'
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7CC363] transition"
                  >
                    Annuleren
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Security Guidelines Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Beveiligingsrichtlijnen</h3>
              
              <div className="space-y-4 text-sm text-blue-800">
                <div>
                  <h4 className="font-medium mb-2">Wachtwoordeisen:</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Minimaal 8 tekens</li>
                    <li>Minimaal één hoofdletter</li>
                    <li>Minimaal één kleine letter</li>
                    <li>Minimaal één cijfer</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Beste praktijken:</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Gebruik een uniek wachtwoord voor dit account</li>
                    <li>Gebruik geen persoonlijke informatie</li>
                    <li>Wijzig uw wachtwoord regelmatig</li>
                    <li>Deel uw wachtwoord nooit met anderen</li>
                  </ul>
                </div>

                <div className="pt-4 border-t border-blue-300">
                  <p className="text-xs text-blue-700">
                    <strong>Tip:</strong> Overweeg het gebruik van een wachtwoordmanager om sterke, unieke wachtwoorden te genereren en op te slaan.
                  </p>
                </div>
              </div>
            </div>

            {/* User Info Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Informatie</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-600">Email:</span>
                  <p className="font-medium text-gray-900 break-words">{user?.email}</p>
                </div>
                <div>
                  <span className="text-gray-600">Account ID:</span>
                  <p className="font-mono text-xs text-gray-900 break-all">{user?.id}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountSettingsPasswordManagement;